import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { updateBooking } from '@hqo/service-booking-client';
import { EventBookingStatus } from '@hqo/service-booking-types';

import { useApi } from 'app/shared/contexts/api';

export const CHECK_IN_MUTATION_KEY = 'activity-check-in';

export interface CheckInVariables {
  /** The event the booking belongs to — service-booking scopes the booking under it. */
  eventUuid: string;
  /** The booking being checked in. In the feed this is the activity item's own `uuid`. */
  bookingUuid: string;
}

/**
 * Self check-in for an event booking, through service-booking's own client rather than a hand-built
 * url — SBS owns and versions that route (`PATCH /service-booking/v1/events/:e/bookings/:b`).
 *
 * Deliberately NOT optimistic, unlike the rating stars. The service re-validates that self check-in
 * is enabled and that `now` is inside the window, so a rejection is a real possibility the user has
 * to be told about — which is the whole point of the failure modal. Showing success first and
 * retracting it would be worse than waiting.
 *
 * Refetching the feed is deliberately NOT done here. The projection is eventually consistent (a
 * Kafka hop plus the OpenSearch refresh interval), so invalidating on success would refetch the
 * pre-check-in status and flip the pill back. The caller refetches when the user dismisses the
 * outcome modal, which is both later and a moment the user expects the list to update.
 */
export const useCheckInMutation = (): UseMutationResult<void, Error, CheckInVariables> => {
  const { apiUrl, getAuthTokenValue } = useApi();

  return useMutation<void, Error, CheckInVariables>({
    mutationKey: [CHECK_IN_MUTATION_KEY],
    mutationFn: async ({ eventUuid, bookingUuid }: CheckInVariables) => {
      const result = await updateBooking(
        {
          baseUrl: apiUrl,
          eventUuid,
          bookingUuid,
          body: { status: EventBookingStatus.CHECKED_IN },
        },
        { authToken: await getAuthTokenValue() },
      );

      if (!result.isSuccess()) {
        throw result.errorData;
      }
    },
    // No retry: the failures worth surfacing here are all deterministic — check-in disabled, outside
    // the window, already checked in, or the event was cancelled. Retrying re-earns the same 400.
    retry: false,
  });
};
